// ---------------------------------------------------------------------------
// Wissenslecks und Enthüllungen (Roadmap AP-43, PF 25) – Prüfungen über das Wissen (knowledge.ts):
//  - Wissensleck: Eine Figur benutzt etwas, das sie laut Wissensmodell noch nicht weiß. Benutzen heißt: Ihre Rede bzw. ihr Gedanke
//    gehört zugleich dem Gewussten (`[„Der Brief ist gefälscht!“]{@clara @brief rede}`) oder verknüpft es darin. Als Gewusstes zählt nur,
//    was irgendwo erfahren bzw. enthüllt wird. Erfährt die Figur es erst später (Weltzeit; ohne vergleichbare Zeiten nach der
//    Lesereihenfolge), ist das ein Widerspruch bzw. ein Hinweis; erfährt sie es nie, ein Hinweis (sie kann es von Anfang an wissen).
//  - Enthüllung, die der Leser schon kennt: dasselbe wird in der Lesereihenfolge noch einmal enthüllt – ein Hinweis (oft gewollt).
//  - Nie enthüllt: Figuren erfahren etwas, dem Leser wird es nie enthüllt – ein Hinweis, nur wenn die Geschichte überhaupt enthüllt.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { isKnower, learningsIn, type Learning } from './knowledge.js';
import { draftDocOf, narrativeOrder } from './narrative.js';
import { mentionsIn, refIndex } from './refs.js';
import { isScene } from './scene.js';
import { spansIn, type SpanRole } from './spans.js';
import { sceneBefore, type Certainty } from './stateConflicts.js';
import type { Entity, IgnoredTerm, PriorSource, StoryNode } from './types.js';

/** Rollen, in denen eine Figur Wissen benutzt */
export const USE_ROLES: readonly SpanRole[] = ['rede', 'gedanke'];

/** Eine Stelle, an der eine Figur Gewusstes benutzt (die erste je Figur und Gewusstem im Text) */
export interface KnowledgeUse {
  knower: string;
  about: string;
  role: SpanRole;
  index: number;
}

/** Wie die Prüfungen das Wissen der Geschichte brauchen – einmal je Stand */
interface Env {
  /** Gewusstes: was irgendwo erfahren bzw. enthüllt wird */
  known: Set<string>;
  /** je Figur und Gewusstem die Szenen, in denen sie es erfährt */
  learned: Map<string, Set<string>>;
  /** je Gewusstem die Enthüllungen in Lesereihenfolge (eine je Szene; aus einer früheren Episode zuerst, ohne Szene: `prior`) */
  reveals: Map<string, { sceneId: string; docId: string; prior?: PriorSource }[]>;
  /** Paare (Figur, Gewusstes), die schon aus früheren Episoden gewusst werden */
  priorKnown: Set<string>;
  learnings: Map<string, Learning[]>;
  reading: Map<string, number>;
  refs: Map<string, Entity>;
}
const envs = new WeakMap<StoryCtx, Env>();
const pair = (knower: string, about: string): string => `${knower}\n${about}`;

function envOf(ctx: StoryCtx): Env {
  let env = envs.get(ctx);
  if (env) return env;
  const order = narrativeOrder(ctx);
  env = { known: new Set(), learned: new Map(), reveals: new Map(), priorKnown: new Set(), learnings: new Map(), reading: new Map(order.scenes.map((s, i) => [s.scene.id, i])), refs: refIndex(ctx) };
  for (const p of ctx.story.series?.knowledge ?? []) {
    env.known.add(p.about);
    if (p.knower) env.priorKnown.add(pair(p.knower, p.about));
    else if (!env.reveals.has(p.about)) env.reveals.set(p.about, [{ sceneId: '', docId: '', prior: { episode: p.episode, scene: p.scene } }]);
  }
  for (const scene of ctx.story.nodes.filter(isScene)) {
    const ls = learningsIn(ctx, scene);
    env.learnings.set(scene.id, ls);
    for (const l of ls) {
      for (const a of l.about) env.known.add(a);
      if (l.role === 'erfährt') for (const k of l.knowers) for (const a of l.about) env.learned.set(pair(k, a), (env.learned.get(pair(k, a)) ?? new Set()).add(scene.id));
    }
  }
  for (const { scene } of order.scenes)
    for (const l of env.learnings.get(scene.id) ?? [])
      if (l.role === 'enthüllt')
        for (const a of l.about) {
          const list = env.reveals.get(a) ?? [];
          if (!list.some((r) => r.sceneId === scene.id)) env.reveals.set(a, [...list, { sceneId: scene.id, docId: l.docId }]);
        }
  envs.set(ctx, env);
  return env;
}

/** Ist das ein Gewusstes (wird es irgendwo erfahren bzw. enthüllt)? */
export const isKnowable = (ctx: StoryCtx, entityId: string): boolean => envOf(ctx).known.has(entityId);

/** Wo ein Text (Markdown) Gewusstes benutzt: Rede bzw. Gedanke einer Person, die dem Gewussten gehört oder es darin verknüpft */
export function knowledgeUsesIn(ctx: StoryCtx, text: string): KnowledgeUse[] {
  const env = envOf(ctx);
  const out = new Map<string, KnowledgeUse>();
  for (const s of spansIn(text)) {
    if (!s.role || !USE_ROLES.includes(s.role)) continue;
    const ids = s.keys.map((k) => env.refs.get(k)?.id).filter((id): id is string => !!id);
    const knowers = ids.filter((id) => isKnower(ctx, id));
    const inner = mentionsIn(text.slice(s.textFrom, s.textTo)).map((m) => env.refs.get(m.key)?.id);
    const about = [...new Set([...ids, ...inner].filter((id): id is string => !!id && env.known.has(id) && !knowers.includes(id)))];
    for (const k of knowers) for (const a of about) if (!out.has(pair(k, a))) out.set(pair(k, a), { knower: k, about: a, role: s.role, index: s.from });
  }
  return [...out.values()];
}

/**
 * Weiß `knower` zur Zeit von `scene` schon, was er dort benutzt? null: ja (oder nicht bestimmbar – auch, wenn er es in derselben Szene
 * erfährt); sonst, wie sicher er es erst später erfährt (`learnedIn`: die erste dieser Szenen) bzw. `never`.
 */
export function knowledgeLeak(ctx: StoryCtx, scene: StoryNode, knower: string, about: string): { certainty: Certainty | 'never'; learnedIn: string | null } | null {
  const env = envOf(ctx);
  // aus einer früheren Episode gewusst
  if (env.priorKnown.has(pair(knower, about))) return null;
  const where = [...(env.learned.get(pair(knower, about)) ?? [])].map((id) => ctx.nodes.get(id)).filter((n): n is StoryNode => !!n);
  if (where.length === 0) return { certainty: 'never', learnedIn: null };
  let worst: Certainty = 'sure';
  let first: StoryNode | null = null;
  for (const l of where) {
    if (l.id === scene.id) return null;
    const after = sceneBefore(ctx, scene, l, env.reading);
    // vorher, gleichzeitig oder unklar: er kann es wissen
    if (!after) return null;
    if (after === 'likely') worst = 'likely';
    if (!first || sceneBefore(ctx, l, first, env.reading)) first = l;
  }
  return { certainty: worst, learnedIn: first!.id };
}

const keyOf = (ctx: StoryCtx, id: string): string => ctx.entities.get(id)?.refKey ?? id;

/** „Ist Absicht“ für ein Wissensleck: diese Figur und dieses Gewusste, gemerkt im Draft */
export const leakIgnore = (ctx: StoryCtx, l: Pick<KnowledgeUse, 'knower' | 'about'>): IgnoredTerm => ({ term: '~wissen', before: `@${keyOf(ctx, l.knower)}`, after: `@${keyOf(ctx, l.about)}` });

/** Wissenslecks im Draft einer Szene (ohne „ist Absicht“) */
export function knowledgeLeaks(ctx: StoryCtx, scene: StoryNode): (KnowledgeUse & { certainty: Certainty | 'never'; learnedIn: string | null })[] {
  const draft = draftDocOf(scene);
  if (!draft || !draft.text.includes(']{@')) return [];
  return knowledgeUsesIn(ctx, draft.text).flatMap((u) => {
    const entry = leakIgnore(ctx, u);
    if (draft.ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after)) return [];
    const leak = knowledgeLeak(ctx, scene, u.knower, u.about);
    return leak ? [{ ...u, ...leak }] : [];
  });
}

/** Enthüllungen einer Szene, die der Leser schon kennt: je Gewusstem die erste frühere Enthüllung (leer: in einer früheren Episode) */
export function repeatedReveals(ctx: StoryCtx, scene: StoryNode): { about: string; docId: string; firstSceneId: string; prior?: PriorSource }[] {
  const env = envOf(ctx);
  const out: { about: string; docId: string; firstSceneId: string; prior?: PriorSource }[] = [];
  for (const [about, list] of env.reveals) {
    const i = list.findIndex((r) => r.sceneId === scene.id);
    const first = list[0]!;
    if (i > 0) out.push({ about, docId: list[i]!.docId, firstSceneId: first.sceneId, ...(first.prior ? { prior: first.prior } : {}) });
  }
  return out;
}

/**
 * Was Figuren erfahren, dem Leser aber nie enthüllt wird – je Gewusstem einmal, an der ersten Szene (Lesereihenfolge, sonst irgendeine),
 * in der es eine Figur erfährt. Nur, wenn die Geschichte überhaupt enthüllt.
 */
export function neverRevealed(ctx: StoryCtx): { about: string; sceneId: string; docId: string }[] {
  const env = envOf(ctx);
  if (env.reveals.size === 0) return [];
  const first = new Map<string, { sceneId: string; docId: string; at: number }>();
  for (const [sceneId, ls] of env.learnings)
    for (const l of ls) {
      if (l.role !== 'erfährt') continue;
      const at = env.reading.get(sceneId) ?? Number.MAX_SAFE_INTEGER;
      for (const a of l.about) if (!env.reveals.has(a) && (!first.has(a) || at < first.get(a)!.at)) first.set(a, { sceneId, docId: l.docId, at });
    }
  return [...first].map(([about, f]) => ({ about, sceneId: f.sceneId, docId: f.docId }));
}
